import React, { useCallback, useState } from "react";
import GenericAlertModal from "../GenericAlertModal";
import { useBeneficiaryTerm } from "../../../hooks/useBeneficiaryTerm";
import {
	ENROLLMENT_LIMIT_TITLE,
	EnrollmentLimitDetails,
	ORGANIZATION_UNIT_SCOPE,
	buildEnrollmentLimitMessage,
	getEnrollmentLimitDetails,
} from "./enrollmentLimit";

interface EnrollmentLimitDialogProps
	extends Omit<EnrollmentLimitDetails, "scope"> {
	/** SIMS-9207: "ORGANIZATION_UNIT" picks the per-unit wording; anything else the across one. */
	scope?: string;
	/** SIMS-9208: the account's word for the person, already lower-cased. */
	person?: string;
	onAccept: () => void;
}

/**
 * SIMS-9205 — "Enrollment limit reached", opened over the enrollment form when the server refuses
 * a new record. One "Accept" button; Escape acts as Accept; a click outside does nothing, so the
 * coordinator always acknowledges it (no `onCancel`). Accepting closes only this dialog: the form
 * underneath stays open with its values, and nothing was created.
 */
const EnrollmentLimitDialog: React.FC<EnrollmentLimitDialogProps> = ({
	count,
	maximum,
	scope,
	organizationUnitName,
	person,
	onAccept,
}) => (
	<GenericAlertModal
		title={ENROLLMENT_LIMIT_TITLE}
		description={buildEnrollmentLimitMessage({
			count,
			maximum,
			scope:
				scope === ORGANIZATION_UNIT_SCOPE ? ORGANIZATION_UNIT_SCOPE : undefined,
			organizationUnitName,
			person,
		})}
		submitText="Accept"
		onClick={onAccept}
		onEscapeKeyDown={onAccept}
		dataTestId="enrollment-limit-dialog"
	/>
);

/**
 * The forms' side of it: call `handleEnrollmentLimitError(error)` first in the submit's catch —
 * it opens the dialog and returns `true` for the limit refusal, `false` for anything else (which
 * keeps its existing handling) — and render `enrollmentLimitDialog`.
 */
export const useEnrollmentLimitDialog = (): {
	handleEnrollmentLimitError: (error: unknown) => boolean;
	enrollmentLimitDialog: React.ReactElement | null;
} => {
	const [details, setDetails] = useState<EnrollmentLimitDetails | null>(null);
	// SIMS-9208: the account's own word ("Beneficiary Terminology", singular), lower case
	// mid-sentence as on every other screen; "Beneficiary" → "beneficiary" when none is set.
	const { singular } = useBeneficiaryTerm();

	const handleEnrollmentLimitError = useCallback((error: unknown): boolean => {
		const limit = getEnrollmentLimitDetails(error);
		if (!limit) return false;
		setDetails(limit);
		return true;
	}, []);

	const enrollmentLimitDialog = details ? (
		<EnrollmentLimitDialog
			count={details.count}
			maximum={details.maximum}
			scope={details.scope}
			organizationUnitName={details.organizationUnitName}
			person={singular.toLowerCase()}
			onAccept={() => setDetails(null)}
		/>
	) : null;

	return { handleEnrollmentLimitError, enrollmentLimitDialog };
};

export default EnrollmentLimitDialog;
